import React, { useEffect, useState } from 'react';
import { translateKnown } from '../../i18n/knownMessages';
import { useTranslation } from 'react-i18next';
import PhotoViewer from './PhotoViewer';
import { imageSrc } from './SupplierImages';
import SupplierReviews from './SupplierReviews';
import api from '../../utils/api';

// Optional join action: pass onAskToJoin (and the group's joinState) to show the button for a logged-in non-member.
// Pass groupId to load the business contact for approved members; the server decides what is returned.
export default function SupplierInfoModal({ supplier: publicSupplier, isOpen, onClose, onAskToJoin, joinState, groupId, trustedContact = false }) {
  const { t } = useTranslation();
  const [photoIndex, setPhotoIndex] = useState(null); // null = gallery closed
  // Private contact, loaded only after the server authorizes it; cleared when the modal closes or the group changes
  const [privateContact, setPrivateContact] = useState(null);
  useEffect(() => {
    setPrivateContact(null);
    if (!isOpen || !groupId) return undefined;
    let active = true;
    // Logout (same tab or another) drops the contact at once and stops any request still in flight
    const drop = () => { active = false; setPrivateContact(null); };
    const onStorage = (e) => { if (e.key === 'token' && !e.newValue) drop(); };
    window.addEventListener('session-cleared', drop);
    window.addEventListener('storage', onStorage);
    api.get(`/api/groups/${groupId}/supplier-contact`)
      .then((res) => { if (active) setPrivateContact(res.data); })
      .catch(() => { if (active) setPrivateContact(null); }); // denied or unavailable: only public information remains
    return () => {
      active = false;
      window.removeEventListener('session-cleared', drop);
      window.removeEventListener('storage', onStorage);
    };
  }, [isOpen, groupId]);
  // Escape closes the dialog, unless the photo viewer on top of it is open (that one handles Escape itself)
  useEffect(() => {
    if (!isOpen || photoIndex !== null) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, photoIndex, onClose]);

  // isOpen decides visibility: callers keep the supplier set after closing, so the supplier alone is not enough
  if (!isOpen || !publicSupplier) return null;
  // Private fields come only from an authorized response. Props never supply them, except on the admin pages (trustedContact),
  // which already load full records for suppliers they manage
  const { email, phone, address, ...publicOnly } = publicSupplier;
  const supplier = { ...publicOnly, ...(trustedContact ? { email, phone, address } : {}), ...(privateContact || {}) };
  const photos = [supplier.images?.primary, ...(supplier.images?.gallery || [])].filter(Boolean);

  // Safely flatten address fields
  const { street, city, state, zip } = supplier.address || {};

  const linkClass = 'text-blue-600 hover:underline break-all';
  const phoneDigits = (supplier.phone || '').replace(/[^\d+]/g, '');
  const addressLine = [street, city, state, zip].filter(Boolean).join(', ');
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressLine)}`;

  const summary = supplier.ratingCount
    ? t('supplierInfo.ratingSummary', { count: supplier.ratingCount, avg: Number(supplier.ratingAverage).toFixed(1) })
    : t('groups.card.noReviews');

  // Own card shell: the header and the Ask to Join / Close footer stay put while the body scrolls
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div role="dialog" aria-modal="true" aria-label={t('supplierInfo.title')} className="bg-white rounded-lg shadow-lg max-w-md w-full max-h-[85vh] flex flex-col">
        <div className="p-6 pb-3 border-b">
          <h2 className="text-lg font-semibold mb-1 break-words [overflow-wrap:anywhere]">{t('supplierInfo.title')}</h2>
          <p className="font-medium text-gray-800 break-words [overflow-wrap:anywhere]">{supplier.name || "N/A"}</p>
          <p className="text-sm text-gray-700">{summary}</p>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-4 break-words [overflow-wrap:anywhere]">
          {photos.length > 0 && (
            <div>
              <img src={imageSrc(photos[0])} alt={supplier.name || "Supplier"} className="w-full max-h-48 object-cover rounded border" />
              <button type="button" onClick={() => setPhotoIndex(0)} className="mt-1 text-blue-600 hover:underline text-sm">
                {t('supplierInfo.viewPhotos')} ({photos.length})
              </button>
            </div>
          )}

          <div className="space-y-2">
            <p>
              <strong>{t('supplierInfo.email')}</strong>{" "}
              {supplier.email ? (
                <a href={`mailto:${encodeURIComponent(supplier.email)}`} className={linkClass}>{supplier.email}</a>
              ) : (
                <span className="text-gray-400">{t('common.na')}</span>
              )}
            </p>
            <p>
              <strong>{t('supplierInfo.phone')}</strong>{" "}
              {phoneDigits ? (
                <a href={`tel:${encodeURIComponent(phoneDigits)}`} className={linkClass}>{supplier.phone}</a>
              ) : (
                <span className="text-gray-400">{t('common.na')}</span>
              )}
            </p>
            <div>
              <strong>{t('supplierInfo.address')}</strong>
              <div className="mt-1">
                {addressLine ? (
                  <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className={linkClass} title={t('supplierInfo.openMaps')}>
                    {street && <div>{street}</div>}
                    {(city || state || zip) && <div>{[city, state, zip].filter(Boolean).join(", ")}</div>}
                  </a>
                ) : (
                  <span className="text-gray-400">{t('supplierInfo.noAddress')}</span>
                )}
              </div>
            </div>
          </div>

          <SupplierReviews supplier={supplier} showSummary={false} />
        </div>

        <div className="p-4 border-t space-y-2">
          {onAskToJoin && (() => {
            const status = joinState?.status || "idle";
            return (
              <div>
                <button
                  type="button"
                  onClick={onAskToJoin}
                  disabled={["checking", "sending", "sent", "pending"].includes(status)}
                  className="w-full px-3 py-2 rounded bg-green-500 text-white hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {["pending", "sent"].includes(status) ? t('joinStatus.requestPending') : t('joinStatus.askToJoin')}
                </button>
                {status === "checking" && <p className="mt-2 text-sm text-gray-600">{t('joinStatus.checking')}</p>}
                {status === "sending" && <p className="mt-2 text-sm text-gray-600">{t('joinStatus.sending')}</p>}
                {status === "sent" && <p className="mt-2 text-sm text-green-700">{translateKnown(t, joinState.message)}</p>}
                {status === "pending" && <p className="mt-2 text-sm text-amber-700">{translateKnown(t, joinState.message)}</p>}
                {status === "error" && <p className="mt-2 text-sm text-red-600">{translateKnown(t, joinState.message)}</p>}
              </div>
            );
          })()}
          <div className="flex justify-end">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">{t('common.ok')}</button>
          </div>
        </div>
      </div>
      {photoIndex !== null && (
        <PhotoViewer photos={photos} index={photoIndex} onIndex={setPhotoIndex} onClose={() => setPhotoIndex(null)} />
      )}
    </div>
  );
}
